import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Display } from '@/components/ui/Typography';
import { isChargerSelectable } from '@/components/points/ChargerStatusBadge';
import { ConnectorRow } from '@/components/stations/ConnectorRow';
import { TypeTile } from '@/components/stations/TypeTile';
import { ChargerLineRow } from '@/components/stations/ChargerLineRow';
import { AmenityTiles } from '@/components/stations/AmenityTiles';
import { PeakHoursChart } from '@/components/stations/PeakHoursChart';
import { RoutesButton } from '@/components/stations/RoutesButton';
import { TariffPrice } from '@/components/stations/TariffPrice';
import { connectorsByType } from '@/lib/connectorGroups';
import { hasConnectorLetters } from '@/lib/connectorLabel';
import { countAvailableConnectors, peakKw, stationTariff } from '@/lib/stationStats';
import { formatConnectorType } from '@/api/points';
import { formatDistance } from '@/lib/geo';
import type { StationDetailOut } from '@/api/stations';

interface Props {
  station: StationDetailOut;
  distanceKm: number | null;
  onClose: () => void;
}

export function HomeStationSheetExpanded({ station, distanceKm, onClose }: Props) {
  const navigate = useNavigate();
  const [openType, setOpenType] = useState<string | null>(null);
  const peak = Math.round(peakKw(station));
  const multiCharger = station.chargers.length > 1;
  const { available, total } = countAvailableConnectors(station);
  const hasCoords = station.latitude !== null && station.longitude !== null;
  // Preço no rodapé só com 1 carregador (Tarifa no Sheet Map-First.html ·
  // "Preço no rodapé, junto de Rotas") — com vários, a tarifa pode variar
  // por carregador e já aparece no detalhamento por tipo.
  const footerTariff = multiCharger ? null : stationTariff(station);

  // Trocar de estação sem passar por "peek" (tocar outro pin com a sheet já
  // expandida) não remonta este componente — sem isso, o detalhamento por
  // tipo aberto na estação anterior ficaria preso na nova.
  useEffect(() => {
    setOpenType(null);
  }, [station.id]);

  // G · Resumo por tipo → abre a estação (feedback Saulo 2026-07-07,
  // mapfirst-multi-noscroll.jsx): estação com vários carregadores mostra os
  // TIPOS de conector como quadrados; tocar abre o detalhamento por tipo.
  if (multiCharger && openType) {
    return (
      <StationTypeDetail
        station={station}
        type={openType}
        onBack={() => setOpenType(null)}
        onClose={onClose}
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex flex-shrink-0 items-start gap-3">
        {station.photo_url ? (
          <img
            src={station.photo_url}
            alt=""
            className="h-[46px] w-[46px] flex-shrink-0 rounded-2xl object-cover"
          />
        ) : (
          <div className="h-[46px] w-[46px] flex-shrink-0 rounded-2xl bg-bow-royal/5" />
        )}
        <div className="min-w-0 flex-1">
          <Display size={21}>{station.name}</Display>
          <div className="mt-1 text-[12.5px] text-bow-royal/60">
            {distanceKm !== null && `${formatDistance(distanceKm)} · `}
            {multiCharger
              ? `${station.chargers.length} carregadores`
              : peak > 0
                ? `até ${peak} kW`
                : station.address}
          </div>
        </div>
        {/* Selo de livres/total só quando tem vários carregadores — num
            carregador só a status pill de cada conector já basta
            (mapfirst-multi.jsx · header). */}
        {multiCharger && total > 0 && (
          <span
            className="flex flex-shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold"
            style={{
              background: available > 0 ? 'rgba(0,192,114,0.12)' : 'rgba(0,27,116,0.06)',
              color: available > 0 ? '#008A4F' : 'rgba(0,27,116,0.55)'
            }}
          >
            <span
              className="h-[6px] w-[6px] rounded-full"
              style={{ background: available > 0 ? '#00C072' : 'rgba(0,27,116,0.4)' }}
            />
            {available} livres
          </span>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-bow-royal/5 text-bow-royal"
        >
          <CloseIcon />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pb-1">
        {station.chargers.length === 0 ? (
          <p className="text-sm text-bow-royal/65">
            Nenhum carregador disponível neste eletroposto.
          </p>
        ) : multiCharger ? (
          // Vários carregadores: resumo por tipo de conector, não mais por
          // carregador físico — tocar um tipo abre o detalhamento (G).
          <>
            <PeakHoursChart stationId={station.id} />
            <div className="flex gap-2.5">
              {/* Potência maior sempre à esquerda (feedback Saulo 2026-07-08). */}
              {[...connectorsByType(station)]
                .sort((a, b) => b.maxKw - a.maxKw)
                .map((g) => (
                  <TypeTile key={g.type} group={g} onOpen={() => setOpenType(g.type)} />
                ))}
            </div>
          </>
        ) : (
          // 1 carregador só: conectores lado a lado, sem precisar agrupar
          // (mapfirst-a.jsx · modo "selected"). Potência maior à esquerda.
          <div className="flex gap-2">
            {[...station.chargers[0]!.connectors]
              .sort((a, b) => Number(b.max_power_kw) - Number(a.max_power_kw))
              .map((conn) => (
                <ConnectorRow
                  key={conn.id}
                  connector={conn}
                  letterLabel={hasConnectorLetters(station.chargers[0]!.capabilities)}
                  onClick={() => navigate(`/configure/${station.chargers[0]!.id}/${conn.id}`)}
                />
              ))}
          </div>
        )}
      </div>

      <div className="flex flex-shrink-0 flex-col gap-3 border-t border-bow-royal/8 pt-3">
        <AmenityTiles station={station} />
        {!multiCharger && <PeakHoursChart stationId={station.id} />}
        {(footerTariff !== null || hasCoords) && (
          <div className="flex items-center gap-3">
            {footerTariff !== null && <TariffPrice tariff={footerTariff} className="flex-1" />}
            {hasCoords && (
              <RoutesButton latitude={station.latitude!} longitude={station.longitude!} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Detalhamento por tipo de conector — aberto ao tocar um `TypeTile` no
 * resumo. Sem mapa/horário de pico (ficam no resumo); carregadores em linhas
 * compactas + amenidades da Bow em destaque (feedback Saulo 2026-07-07,
 * mapfirst-multi-noscroll.jsx · G · `NscTipoDetalhe`). */
function StationTypeDetail({
  station,
  type,
  onBack,
  onClose
}: {
  station: StationDetailOut;
  type: string;
  onBack: () => void;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const chargers = station.chargers.filter((c) => c.connectors.some((conn) => conn.type === type));
  const typeConnectors = chargers.flatMap((c) => c.connectors).filter((c) => c.type === type);
  const free = typeConnectors.filter((c) => isChargerSelectable(c.state)).length;
  const hasCoords = station.latitude !== null && station.longitude !== null;
  const tariff = chargers.find((c) => c.tariff_per_kwh_brl != null)?.tariff_per_kwh_brl;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex flex-shrink-0 items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          aria-label="Voltar"
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-bow-royal/5 text-bow-royal"
        >
          <BackIcon />
        </button>
        <div className="min-w-0 flex-1">
          <Display size={19}>Conectores {formatConnectorType(type)}</Display>
          <div className="mt-0.5 text-[12px] text-bow-royal/60">
            {station.name} · {free} de {typeConnectors.length}{' '}
            {free === 1 ? 'livre' : 'livres'}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-bow-royal/5 text-bow-royal"
        >
          <CloseIcon />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pb-1">
        {chargers.length === 0 ? (
          <p className="text-sm text-bow-royal/65">
            Nenhum carregador desse tipo disponível no momento.
          </p>
        ) : (
          chargers.map((c, i) => (
            <ChargerLineRow
              key={c.id}
              charger={c}
              index={i}
              onSelectConnector={(connectorId) => navigate(`/configure/${c.id}/${connectorId}`)}
            />
          ))
        )}
      </div>

      <div className="flex flex-shrink-0 flex-col gap-3 border-t border-bow-royal/8 pt-3">
        <AmenityTiles station={station} variant="destaque" />
        {(tariff || hasCoords) && (
          <div className="flex items-center gap-3">
            {tariff && <TariffPrice tariff={tariff} className="flex-1" />}
            {hasCoords && (
              <RoutesButton latitude={station.latitude!} longitude={station.longitude!} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CloseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function BackIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="17"
      height="17"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m14.5 5-7 7 7 7" />
    </svg>
  );
}
